import React, { useEffect, useRef, useState } from 'react';

import { API_FETCHING } from '@nw/api-constants';

import { DangerIcon } from '../../icons/DangerIcon';

import { showErrorToast } from '../ToastUtils/ToastUtils';

// keyCode constants
const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;
const SPACEBAR = 32;

// Doesn't really check if it's a style Object
// Basic implementation to check if it's not a string
// of classNames and is an Object
// TODO: Better implementation
const isStyleObject = (obj) => typeof obj === 'object';
const isNumber = (n) => {
  return /\d+/g.test(n);
};

const SingleOtpInput = (props) => {
  const input = useRef<HTMLInputElement>(null);

  // Focus on first render
  // Only when shouldAutoFocus is true

  useEffect(() => {
    const { focus, shouldAutoFocus } = props;
    const { current: inputEl } = input;

    if (inputEl && focus && shouldAutoFocus) {
      inputEl.focus();
    }
  }, []);

  useEffect(() => {
    const { focus } = props;
    const { current: inputEl } = input;

    if (focus && inputEl && focus) {
      inputEl.focus();
      inputEl.select();
    }
  }, [props.focus, input]);

  const getClasses = (...classes) =>
    classes.filter((c) => !isStyleObject(c) && c !== false).join(' ');

  const getType = () => {
    const { isInputSecure, isInputNum } = props;

    if (isInputSecure) {
      return 'password';
    }

    if (isInputNum) {
      return 'tel';
    }

    return 'text';
  };

  const {
    placeholder,
    separator,
    isLastChild,
    inputStyle,
    focus,
    isDisabled,
    hasErrored,
    errorStyle,
    focusStyle,
    disabledStyle,
    shouldAutoFocus,
    isInputNum,
    index,
    value,
    className,
    isInputSecure,
    onBlur,
    isFocus,
    ...rest
  } = props;

  return (
    <>
      <input
        aria-label={`${index === 0 ? 'Please enter verification code. ' : ''}${
          isInputNum ? 'Digit' : 'Character'
        } ${index + 1}`}
        autoComplete="off"
        style={Object.assign(
          { textAlign: 'center' },
          isStyleObject(inputStyle) && inputStyle,
          focus && isStyleObject(focusStyle) && focusStyle,
          isDisabled && isStyleObject(disabledStyle) && disabledStyle,
          hasErrored && !isFocus && isStyleObject(errorStyle) && errorStyle
        )}
        placeholder={placeholder}
        className={getClasses(
          inputStyle,
          focus && focusStyle,
          isDisabled && disabledStyle,
          hasErrored && !isFocus && errorStyle
        )}
        type={getType()}
        inputmode="numeric"
        pattern={'[0-9]*'}
        maxLength="1"
        ref={input}
        disabled={isDisabled}
        value={value ? value : ''}
        onBlur={onBlur}
        isFocus={isFocus}
        {...rest}
      />
    </>
  );
};

const OtpInput = (props) => {
  const { numInputs = 4, value = '' } = props;

  const [activeInput, setActiveInput] = useState(0);

  const getOtpValue = () => (value ? value.toString().split('') : []);

  const getPlaceholderValue = () => {
    const { placeholder, numInputs } = props;

    if (typeof placeholder === 'string') {
      if (placeholder.length === numInputs) {
        return placeholder;
      }

      if (placeholder.length > 0) {
        console.error(
          'Length of the placeholder should be equal to the number of inputs.'
        );
      }
    }
    return '';
  };

  useEffect(() => {
    if (props.apiStatus === API_FETCHING) {
      setActiveInput(-1);
    } else {
      if (value && value.length) {
        if (value.length === numInputs) {
          setActiveInput(value.length - 1);
        } else {
          setActiveInput(value.length);
        }
      } else {
        setActiveInput(0);
      }
    }
  }, [props.apiStatus]);

  // Helper to return OTP from input
  const handleOtpChange = (otp) => {
    const { onChange } = props;
    const otpValue = otp.join('');

    onChange(otpValue);
  };

  const isInputValueValid = (value) => {
    const isTypeValid = props.isInputNum
      ? !isNaN(parseInt(value, 10))
      : typeof value === 'string';

    return isTypeValid && value.trim().length === 1;
  };

  // Focus on input by index
  const focusInput = (input) => {
    const { numInputs } = props;
    const activeInput = Math.max(Math.min(numInputs - 1, input), 0);
    setActiveInput(activeInput);
  };

  // Focus on next input
  const focusNextInput = () => {
    focusInput(activeInput + 1);
  };

  // Focus on previous input
  const focusPrevInput = () => {
    focusInput(activeInput - 1);
  };

  // Change OTP value at focused input
  const changeCodeAtFocus = (value) => {
    const otp = getOtpValue();
    otp[activeInput] = value[0];
    handleOtpChange(otp);
  };

  // Handle pasted OTP
  const handleOnPaste = (e) => {
    e.preventDefault();
    const { numInputs, isDisabled } = props;

    if (isDisabled) {
      return;
    }

    const otp = getOtpValue();
    let nextActiveInput = activeInput;

    // Get pastedData in an array of max size (num of inputs - current position)
    const pastedData = e.clipboardData
      .getData('text/plain')
      .slice(0, numInputs - activeInput)
      .split('');
    if (!isNumber(pastedData.join(''))) {
      props.showToast();
    }
    // Paste data from focused input onwards
    for (let pos = 0; pos < numInputs; ++pos) {
      if (pos >= activeInput && pastedData.length > 0) {
        otp[pos] = pastedData.shift();
        nextActiveInput++;
      }
    }

    setActiveInput(nextActiveInput);
    focusInput(nextActiveInput);
    handleOtpChange(otp);
  };

  const handleOnChange = (e) => {
    const { value } = e.target;
    if (!isNumber(value)) {
      if (props.showToast) {
        props.showToast();
      } else {
        showErrorToast(props.t('common.pleaseEnterNumbers'), {
          icon: () => <DangerIcon />
        });
      }
    }
    if (isInputValueValid(value)) {
      changeCodeAtFocus(value);
    }
  };

  // Handle cases of backspace, delete, left arrow, right arrow, space
  const handleOnKeyDown = (e) => {
    if (e.keyCode === BACKSPACE || e.key === 'Backspace') {
      e.preventDefault();
      changeCodeAtFocus('');
      focusPrevInput();
    } else if (e.keyCode === DELETE || e.key === 'Delete') {
      e.preventDefault();
      changeCodeAtFocus('');
    } else if (e.keyCode === LEFT_ARROW || e.key === 'ArrowLeft') {
      e.preventDefault();
      focusPrevInput();
    } else if (e.keyCode === RIGHT_ARROW || e.key === 'ArrowRight') {
      e.preventDefault();
      if (value[activeInput + 1]) focusNextInput();
    } else if (
      e.keyCode === SPACEBAR ||
      e.key === ' ' ||
      e.key === 'Spacebar' ||
      e.key === 'Space'
    ) {
      e.preventDefault();
    }
  };

  // The content may not have changed, but some input took place hence change the focus
  const handleOnInput = (e) => {
    if (isInputValueValid(e.target.value)) {
      focusNextInput();
    } else {
      // This is a workaround for dealing with keyCode "229 Unidentified" on Android.

      if (!props.isInputNum) {
        const { nativeEvent } = e;

        if (
          nativeEvent.data === null &&
          nativeEvent.inputType === 'deleteContentBackward'
        ) {
          e.preventDefault();
          changeCodeAtFocus('');
          focusPrevInput();
        }
      }
    }
  };

  const renderInputs = () => {
    const {
      numInputs,
      inputStyle,
      focusStyle,
      separator,
      isDisabled,
      disabledStyle,
      hasErrored,
      errorStyle,
      shouldAutoFocus,
      isInputNum,
      isInputSecure,
      className,
      onBlur,
      isFocus
    } = props;

    const inputs: any = [];
    const otp = getOtpValue();
    const placeholder = getPlaceholderValue();
    const dataCy = props['data-cy'];
    const dataTestId = props['data-testid'];

    for (let i = 0; i < numInputs; i++) {
      inputs.push(
        <SingleOtpInput
          placeholder={placeholder && placeholder[i]}
          key={i}
          index={i}
          disabled={activeInput < i && !otp[i]}
          focus={activeInput === i}
          value={otp && otp[i]}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          onInput={handleOnInput}
          onPaste={handleOnPaste}
          onFocus={(e) => {
            setActiveInput(i);
            e.target.select();
          }}
          separator={separator}
          inputStyle={inputStyle}
          focusStyle={focusStyle}
          isLastChild={i === numInputs - 1}
          isDisabled={isDisabled}
          disabledStyle={disabledStyle}
          hasErrored={hasErrored}
          errorStyle={errorStyle}
          shouldAutoFocus={shouldAutoFocus}
          isInputNum={isInputNum}
          isInputSecure={isInputSecure}
          className={className}
          data-cy={dataCy && `${dataCy}-${i}`}
          data-testid={dataTestId && `${dataTestId}-${i}`}
          onBlur={onBlur}
          isFocus={isFocus}
        />
      );
    }

    return inputs;
  };

  const { containerStyle } = props;

  return (
    <div
      style={Object.assign(
        { display: 'flex' },
        isStyleObject(containerStyle) && containerStyle
      )}
      className={!isStyleObject(containerStyle) ? containerStyle : ''}
    >
      {renderInputs()}
    </div>
  );
};

export default OtpInput;
